import React, { useEffect, useRef, useState, Dispatch, SetStateAction } from 'react';
import CompanyDetailsTooltip from '../../../../components/common/CompanyDetailsTooltip';
import CompanyLogo from '../../../../components/common/CompanyLogo';
import CustomCheckbox from '../../../../components/common/CustomCheckbox';
import SideDrawer from '../../../../components/common/SideDrawer';
import TableLoader from '../../../../components/common/TableLoader';
import { Lead, useLeads } from '../../../../hooks/useLeads';
import platforms from '../../../../assets/platforms.svg';
import intent from '../assets/intent.svg';
import locationIcon from '../assets/location.svg';
import website from '../assets/web.svg';
import empty from '../../../../assets/empty.svg';
import createIcon from '../../../../assets/create.svg';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import apiHandler from '../../../../services/apiServices';

interface TagTooltipProps {
  tags: string[];
  isVisible: boolean;
}

// Define color combinations for tags
const tagColors = [
  { bg: 'bg-[#E0F7FA]', text: 'text-[#00796B]' }, 
  { bg: 'bg-[#C8E6C9]', text: 'text-[#388E3C]' }, 
  { bg: 'bg-[#F8BBD0]', text: 'text-[#C2185B]' }, 
  { bg: 'bg-[#F5F5F5]', text: 'text-[#757575]' }, 
  { bg: 'bg-[#FFF9C4]', text: 'text-[#F57F17]' }, 
  { bg: 'bg-[#BBDEFB]', text: 'text-[#1976D2]' }, 
  { bg: 'bg-[#E1BEE7]', text: 'text-[#8E24AA]' },
  { bg: 'bg-[#FFCCBC]', text: 'text-[#D84315]' },
];

// Function to get consistent color for a tag
const getTagColor = (tag: string) => {
  const index = Math.abs(tag.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % tagColors.length;
  return tagColors[index];
};

const TagTooltip: React.FC<TagTooltipProps> = ({ tags, isVisible }) => {
  if (!isVisible) return null;

  return (
    <div
      className="fixed z-50 pointer-events-none animate-fadeIn"
      style={{
        transform: 'translate(0, -100%)',
      }}
    >
      <div className="bg-white p-3 rounded-md shadow-lg max-w-[300px] border border-[#EEEEEE]">
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, index) => {
            const { bg, text } = getTagColor(tag);
            return (
              <span
                key={index}
                className={`px-2 py-1 rounded-full text-xs whitespace-nowrap ${bg} ${text}`}
              >
                {tag.trim()}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface SignalsTooltipProps {
  signals: Lead['signals'];
  isVisible: boolean;
}

const SignalsTooltip: React.FC<SignalsTooltipProps> = ({ signals, isVisible }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Find the closest table container
    if (tooltipRef.current) {
      const table = tooltipRef.current.closest('.overflow-y-auto');
      if (table) {
        tableRef.current = table as HTMLElement;
      }
    }
  }, []);

  const handleMouseMove = (e: MouseEvent) => {
    if (tooltipRef.current && tableRef.current) {
      const tooltip = tooltipRef.current;
      const table = tableRef.current;
      const tooltipWidth = tooltip.offsetWidth;
      const tooltipHeight = tooltip.offsetHeight;
      const padding = 10;

      // Get table boundaries
      const tableRect = table.getBoundingClientRect();

      // Calculate initial position
      let x = e.clientX + 15;
      let y = e.clientY - 10;

      // Check boundaries and adjust position
      if (x + tooltipWidth > tableRect.right - padding) {
        x = e.clientX - tooltipWidth - 15;
      }
      if (y - tooltipHeight < tableRect.top + padding) {
        y = e.clientY + 20;
      }
      if (x < tableRect.left + padding) x = tableRect.left + padding;
      
      // Convert to page coordinates
      const pageX = x + window.pageXOffset;
      const pageY = y + window.pageYOffset;

      setPosition({ x: pageX, y: pageY });
    }
  };

  useEffect(() => {
    if (isVisible) {
      window.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div
      ref={tooltipRef}
      className="fixed z-50 pointer-events-none animate-fadeIn"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`
      }}
    >
      <div className="bg-white p-3 rounded-[16px] shadow-lg max-w-[300px] border border-[#EEEEEE]">
        <div className="flex flex-col gap-4">
          {signals.map((signal, index) => (
            <div key={index} className="flex flex-col">
              {signal.alignment_score && (
                <span className={`
                  self-start px-2 h-[20px] rounded-[8px] text-[10px] font-light flex items-center
                  ${signal.alignment_score === 'high' 
                    ? 'bg-[#E8F5E9] text-[#388E3C]' 
                    : signal.alignment_score === 'medium' 
                    ? 'bg-[#FFF3E0] text-[#F57F17]'
                    : 'bg-[#FFEBEE] text-[#D32F2F]'
                  }
                `}>
                  {signal.alignment_score.charAt(0).toUpperCase() + signal.alignment_score.slice(1)}
                </span>
              )}
              <div className="text-sm text-[#222222]  font-normal">
                {signal.signal}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface LeadsTableProps {
  sidebarOpen: boolean;
  selectedLeads: Lead[];
  setSelectedLeads: Dispatch<SetStateAction<Lead[]>>;
  leads: Lead[];
  totalLeads: number;
  isLoading: boolean;
  fetchNextPage: () => Promise<any>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  sortOption: string;
  onCloseSideDrawer: () => void;
}

const LeadsTable: React.FC<LeadsTableProps> = ({ sidebarOpen, selectedLeads, setSelectedLeads, leads, totalLeads, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, sortOption }) => {
  const itemsPerPage = 20;
  const [allFetchedLeads, setAllFetchedLeads] = useState<Lead[]>([]);
  const [isNavigating, setIsNavigating] = useState(false);
  const [intentSignalId, setIntentSignalId] = useState<string | undefined>();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  useEffect(() => {
    const params = new URLSearchParams(location.search as URLSearchParams);
    const signalId = params.get('intent_signal_id');
    setIntentSignalId(signalId || undefined);
  }, [location.search]);

  const { 
    leads: currentLeads, 
    totalLeads: currentTotalLeads, 
    isLoading: currentIsLoading, 
    error, 
    fetchNextPage: currentFetchNextPage, 
    hasNextPage: currentHasNextPage,
    isFetchingNextPage: currentIsFetchingNextPage
  } = useLeads(itemsPerPage, sortOption, intentSignalId);

  useEffect(() => {
    if (currentLeads.length > 0) {
      setAllFetchedLeads(prev => {
        // Check if we already have all these leads
        const hasAllLeads = currentLeads.every(lead => 
          prev.some(p => p._id === lead._id)
        );
        
        if (hasAllLeads) {
          return prev; // Return existing state if no new leads
        }
        
        // Otherwise, add new leads
        const newLeads = [...prev];
        currentLeads.forEach(lead => {
          if (!newLeads.find(l => l._id === lead._id)) {
            newLeads.push(lead);
          }
        });
        return newLeads;
      });
    }
  }, [currentLeads]);

  useEffect(() => {
    const path = window.location.pathname;
    const leadId = path.match(/\/accounts\/(.+)/)?.[1];
    
    if (leadId) {
      const lead = allFetchedLeads.find(l => l._id === leadId);
      if (lead) {
        setSelectedLead(lead);
        setIsSideDrawerOpen(true);
      }
    }
  }, [allFetchedLeads]);

  const [hoveredSignal, setHoveredSignal] = useState<string | null>(null);
  const [hoveredIntentSignal, setHoveredIntentSignal] = useState<string | null>(null);
  const [expandedCategoryIndex, setExpandedCategoryIndex] = useState<number | null>(null);
  const [hoveredCompany, setHoveredCompany] = useState<string | null>(null);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isSideDrawerOpen, setIsSideDrawerOpen] = useState(false);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedLeads(currentLeads);
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (index: number, checked: boolean) => {
    if (checked) {
      setSelectedLeads([...selectedLeads, currentLeads[index]]);
    } else {
      setSelectedLeads(selectedLeads.filter(lead => lead._id !== currentLeads[index]._id));
    }
  };

  const handleCompanyClick = (lead: Lead) => {
    setSelectedLead(lead);
    setIsSideDrawerOpen(true);
    window.history.pushState({}, '', `/accounts/${lead._id}`);
  };

  const handleCloseSideDrawer = () => {
    setIsSideDrawerOpen(false);
    window.history.pushState({}, '', '/accounts');
  };

  const handleNavigate = async (direction: 'up' | 'down') => {
    setIsNavigating(true);
    const currentIndex = allFetchedLeads.findIndex((l: Lead) => l._id === selectedLead?._id);
    let newIndex;
    
    if (direction === 'up') {
      newIndex = Math.max(0, currentIndex - 1);
    } else {
      newIndex = Math.min(allFetchedLeads.length - 1, currentIndex + 1);
    }

    // If we're close to the end and there are more leads to fetch
    if (direction === 'down' && 
        newIndex >= allFetchedLeads.length - 3 && 
        currentHasNextPage) {
      await currentFetchNextPage();
    }
    
    setSelectedLead(allFetchedLeads[newIndex]);
    setIsNavigating(false);
  };

  const columns = [
    { width: '21%', minWidth: '200px' },
    { width: '30%', minWidth: '250px' },
    { width: '20%', minWidth: '200px' },
    { width: '17%', minWidth: '150px' },
    { width: '17%', minWidth: '150px' }
  ];

  const totalPages = Math.ceil(totalLeads / itemsPerPage);

  const tableBodyRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && currentHasNextPage && !currentIsFetchingNextPage) {
          currentFetchNextPage();
        }
      },
      { threshold: 0.5 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [currentFetchNextPage, currentHasNextPage, currentIsFetchingNextPage]);

  const navigate = useNavigate();
  
  const handleCreateIntent = () => {
    navigate({ 
      to: '/intent-signals',
      search: { openCreateIntent: 'true' }
    });
  };

  const handleLeadClick = async (leadId: string) => {
    setIsNavigating(true);
    try {
      // Fetch individual lead data
      const response = await apiHandler.get(`/intent-leads/clubbed/${leadId}`);
      queryClient.setQueryData(['intentLead', leadId], response.data);
      
      // Update URL and open drawer
      navigate({ to: `/accounts/${leadId}` });
      setSelectedLead(response.data);
    } catch (error) {
      console.error('Error fetching lead:', error);
    } finally {
      setIsNavigating(false);
    }
  };

  return (
    <>
      <div className="flex flex-col w-full h-[calc(100vh-180px)]">
        <div className="flex-1 w-full overflow-hidden flex flex-col font-">
          <div className="w-full border border-[#EEEEEE] flex flex-col h-full">
          {(isLoading || currentIsLoading) ? (
                <div className="h-full">
                  <TableLoader columns={columns} />
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center h-[calc(100vh-300px)]">
                  <div className="flex flex-col items-center max-w-[440px] text-center bg-white p-8 rounded-2xl shadow-sm border border-[#EEEEEE]">
                    {/* Error Icon */}
                    <div className="w-16 h-16 mb-6 rounded-full bg-[#FEE2E2] flex items-center justify-center">
                      <svg 
                        className="w-8 h-8 text-[#E02C16]" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                        />
                      </svg>
                    </div>

                    <h3 className="text-[20px] font-semibold text-[#222222] mb-3">
                      Error loading leads
                    </h3>
                    
                    <p className="text-[15px] text-[#666666] mb-8 leading-relaxed">
                      We encountered an issue while loading your leads. 
                      Please try again or contact support if the problem persists.
                    </p>

                    <div className="flex gap-4">
                      <button 
                        onClick={() => window.location.reload()}
                        className="inline-flex items-center px-5 py-2.5 bg-[#E02C16] text-white text-sm font-normal rounded-lg hover:bg-[#c72512] transition-colors gap-2"
                      >
                        <svg 
                          className="w-4 h-4" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                          />
                        </svg>
                        Retry
                      </button>
                    </div>
                  </div>
                </div>
              ) : currentLeads.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[calc(100vh-300px)] border border-[#EEEEEE] rounded-lg m-4">
                 <div className="flex flex-col items-center max-w-[400px] text-center">
                    <img src={empty} alt="Empty" className="w-[160px] h-[85px] mb-2" />
                    <div className="flex flex-col items-center justify-center mt-6">
                      <h3 className="text-[16px] font-semibold text-[#000000] mb-3">
                        No Leads Yet - Let's Get Started
                      </h3>
                      <div className="text-[14px] text-[#7A7A7A] font-normal mb-3">
                        Create intent signals to start generating leads
                      </div>
                      <button 
                        onClick={handleCreateIntent}
                        className="flex items-center justify-center w-fit h-fit bg-[#29DC5B] text-[#222222] rounded-lg text-[Nunito Sans] text-[14px] font-light gap-[6px] transition-colors"
                      >
                        <div className="flex items-center justify-center gap-[6px] p-[8px]">
                          <img src={createIcon} alt="Create Intent Signal" className="w-[14px] h-[14px] stroke-[#222222]" />
                          <div className="text-[14px] font-normal">Create Intent signal</div>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
            <>
              <table className="w-full table-fixed">
                <thead>
                  <tr className="border-b bg-[#FFFFFF] z-10">
                    <th className="pl-6 text-right w-[40px] min-w-[40px] h-[40px]">
                      <CustomCheckbox
                        checked={!isLoading && selectedLeads.length === currentLeads.length && currentLeads.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className="pl-4 text-left text-sm font-normal text-[#222222] w-[20%] min-w-[200px] border-r border-[#EEEEEE] h-[40px]">
                      Company name
                    </th>
                    <th className="px-4 text-left text-sm font-normal text-[#222222] w-[30%] min-w-[250px] border-r border-[#EEEEEE] h-[40px]">
                      <div className="flex items-center">
                        <img src={intent} alt="Intent" className="w-[14px] h-[14px] mr-2" />
                        <span>Matching intent signals</span>
                      </div>
                    </th>
                    <th className="px-4 text-left text-sm font-normal text-[#222222] w-[20%] min-w-[200px] border-r border-[#EEEEEE] h-[40px]">
                      <div className="flex items-center">
                        <img src={platforms} alt="Platforms" className="w-[14px] h-[14px] mr-2" />
                        <span>Platforms</span>
                      </div>
                    </th>
                    <th className="px-4 text-left text-sm font-normal text-[#222222] w-[15%] min-w-[150px] border-r border-[#EEEEEE] h-[40px]">
                      <div className="flex items-center">
                        <img src={website} alt="Website" className="w-[14px] h-[14px] mr-2" />
                        <span>Website</span>
                      </div>
                    </th>
                    <th className="pl-4 pr-6 text-left text-sm font-normal text-[#222222] w-[15%] min-w-[150px] border-r border-[#EEEEEE] h-[40px]">
                      <div className="flex items-center">
                        <img src={locationIcon} alt="Location" className="w-[14px] h-[14px] mr-2" />
                        <span>Location</span>
                      </div>
                    </th>
                  </tr>
                </thead>
              </table>
              <div 
                ref={tableBodyRef}
                className="flex-1 overflow-y-auto scrollbar-hide min-h-0"
                style={{ 
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none'
                }}
              >
                  <table className="w-full table-fixed">
                    <tbody className="bg-[#FFFFFF]">
                      {currentLeads.map((lead: Lead, index: number) => (
                        <tr 
                          key={index}
                          className="border-b border-[#EEEEEE] hover:bg-[#FFFFFF]"
                        >
                          <td className="pl-6 text-right w-[40px] min-w-[40px] h-[36px]"
                              onClick={(e) => e.stopPropagation()}>
                            <CustomCheckbox
                              checked={selectedLeads.some(lead => lead._id === currentLeads[index]._id)}
                              onChange={(checked) => handleSelectLead(index, checked)}
                            />
                          </td>
                          <td 
                            className="pl-4 text-left text-[14px] font-normal text-[#222222] w-[20%] min-w-[200px] border-r border-[#EEEEEE] h-[36px]"
                          >
                            <div className="flex items-center group cursor-pointer w-fit max-w-[100%] pr-4">
                              <CompanyLogo
                                name={lead.company_data.name}
                                website={lead.company_data.website}
                                existingLogo={lead.company_data.social_media_company_data[0]?.logo}
                                className="w-[16px] h-[16px] mr-2 flex-shrink-0"
                              />
                              <div 
                                className="group-hover:underline truncate"
                                onMouseEnter={() => setHoveredCompany(lead.company_data.name)}
                                onMouseLeave={() => setHoveredCompany(null)}
                                onClick={() => handleCompanyClick(lead)}
                              >
                                {lead.company_data.name}
                              </div>
                              {hoveredCompany === lead.company_data.name && (
                                <CompanyDetailsTooltip 
                                  name={lead.company_data.name}
                                  logo={lead.company_data.social_media_company_data[0]?.logo}
                                  website={lead.company_data.website}
                                  description={lead.company_data.social_media_company_data[0]?.description}
                                  location={lead.company_data.social_media_company_data[0]?.location || 'N/A'}
                                  isVisible={true}
                                />
                              )}
                            </div>
                          </td>
                          <td 
                            className="px-4 text-left text-[14px] font-normal text-[#222222] bg-[#F9FBFE] w-[30%] min-w-[250px] border-r border-[#EEEEEE] h-[36px]"
                          >
                            <div 
                              onMouseEnter={() => setHoveredIntentSignal(lead._id)}
                              onMouseLeave={() => setHoveredIntentSignal(null)}
                              className="flex items-center justify-between w-fit max-w-[100%]"
                            >
                              <div className="truncate max-w-[90%]">
                                {lead.signals.length > 0 ? lead.signals[0].signal : 'No signals'}
                              </div>
                              {lead.signals.length > 1 && (
                                <div className="text-[#7A7A7A] font-normal whitespace-nowrap h-[20px] w-[20px] bg-[#EEEEEE] rounded-1">
                                  +{lead.signals.length - 1}
                                </div>
                              )}
                              {hoveredIntentSignal === lead._id && lead.signals.length > 0 && (
                                <SignalsTooltip 
                                  signals={lead.signals} 
                                  isVisible={true}
                                />
                              )}
                            </div>
                          </td>
                          <td 
                            className="px-4 text-left text-sm font-light text-[#222222] w-[20%] min-w-[200px] border-r border-[#EEEEEE] h-[36px]"
                          >
                            <div className="w-full overflow-x-auto scrollbar-hide">
                              <div className="flex items-center flex-nowrap gap-2 text-[14px] text-[#7A7A7A] font-medium">
                                {lead.company_data.platforms?.map((platform, idx) => (
                                  <span 
                                    key={idx} 
                                    className="whitespace-nowrap"
                                  >
                                    {platform}
                                  </span>
                                )) || 'LinkedIn'}
                              </div>
                            </div>
                          </td>
                          <td 
                            className="px-4 text-left text-[14px] font-light text-[#5D90D4] w-[15%] max-w-[100%] border-r border-[#EEEEEE] h-[36px] truncate"
                          >
                            <a 
                              href={lead.company_data.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#5D90D4] hover:underline rounded-[8px] border border-[#A7CAE9] px-2 py-[3.5px] truncate w-fit max-w-[100%]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              https://{lead.company_data.company_website_data?.domain}
                            </a>
                          </td>
                          <td 
                            className="pl-4 pr-6 text-left text-sm font-light text-[#7A7A7A] w-[15%] min-w-[150px] border-r border-[#EEEEEE] h-[36px]"
                          >
                            <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                              {lead.company_data.social_media_company_data[0]?.location || 'N/A'}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {currentIsFetchingNextPage && (
                        <>
                          <tr className="animate-pulse">
                            <td className="pl-6 w-[40px]">
                              <div className="h-4 w-4 bg-[#EEEEEE] rounded"></div>
                            </td>
                            {columns.map((column, idx) => (
                              <td 
                                key={idx} 
                                className="px-4 border-r border-[#EEEEEE] h-[36px]"
                                style={{ 
                                  width: column.width,
                                  minWidth: column.minWidth
                                }}
                              >
                                <div className="h-4 bg-[#EEEEEE] rounded w-3/4"></div>
                              </td>
                            ))}
                          </tr>
                          <tr className="animate-pulse">
                            <td className="pl-6 w-[40px]">
                              <div className="h-4 w-4 bg-[#EEEEEE] rounded"></div>
                            </td>
                            {columns.map((column, idx) => (
                              <td 
                                key={idx} 
                                className="px-4 border-r border-[#EEEEEE] h-[36px]"
                                style={{ 
                                  width: column.width,
                                  minWidth: column.minWidth
                                }}
                              >
                                <div className="h-4 bg-[#EEEEEE] rounded w-3/4"></div>
                              </td>
                            ))}
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                <div ref={loadMoreRef} className="w-full py-4">
                  {currentIsFetchingNextPage && (
                    <div className="flex justify-center">
                      <div className="animate-pulse w-20 h-4 bg-gray-200 rounded"></div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          </div>
        </div>
      </div>

      <SideDrawer 
        isOpen={isSideDrawerOpen}
        onClose={handleCloseSideDrawer}
        lead={selectedLead}
        currentIndex={selectedLead ? allFetchedLeads.findIndex((l: Lead) => l._id === selectedLead._id) : 0}
        totalLeads={totalLeads}
        onNavigate={handleNavigate}
        isNavigating={isNavigating}
        setIsNavigating={setIsNavigating}
        allLeads={allFetchedLeads}
      />
    </>
  );
};

export default LeadsTable; 